import React, { useState } from 'react';

// Hooks
import { useAuthContext } from '../../context/auth-context';
import { useNavigate } from 'react-router-dom';
import { useAxios } from '../../hooks/use-axios';

// Constants
import { userUrl } from '../../constants';

// Css
import styled from 'styled-components';
import usernameUtils from '../../utils/usernameUtils';
import { WEB_ROLES } from '../../constants/roles';

const Root = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #f4f1e9;
  font-family: 'Avenir Next', 'Trebuchet MS', sans-serif;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const Shell = styled.section`
  width: min(100%, 1120px);
  min-height: 720px;
  display: grid;
  grid-template-columns: minmax(320px, 1.15fr) 0.85fr;
  background-color: #ffffff;
  border: 1px solid rgba(53, 62, 53, 0.45);
  box-shadow: 0 28px 80px rgba(38, 42, 33, 0.14);
  overflow: hidden;

  @media (max-width: 900px) {
    min-height: auto;
    grid-template-columns: 1fr;
  }
`;

const IllustrationPanel = styled.div`
  position: relative;
  min-height: 720px;
  background: linear-gradient(180deg, #244f33 0%, #274e35 100%);
  overflow: hidden;

  @media (max-width: 900px) {
    min-height: 420px;
  }

  @media (max-width: 640px) {
    min-height: 320px;
  }
`;

const PanelGlow = styled.div`
  position: absolute;
  inset: auto auto -12% -12%;
  width: 58%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 72%);
`;

const Moon = styled.div`
  position: absolute;
  top: 94px;
  right: 72px;
  width: 78px;
  height: 78px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #fcffff 0%, #cad2cd 52%, #b4beb8 100%);
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.35);

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 22px;
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: #274e35;
  }

  @media (max-width: 640px) {
    top: 54px;
    right: 36px;
    width: 58px;
    height: 58px;

    &::after {
      left: 16px;
      width: 54px;
      height: 54px;
    }
  }
`;

const StarField = styled.div`
  position: absolute;
  top: 88px;
  left: 62px;
  width: 132px;
  height: 100px;

  @media (max-width: 640px) {
    top: 44px;
    left: 28px;
    transform: scale(0.8);
    transform-origin: top left;
  }
`;

const Star = styled.span`
  position: absolute;
  width: ${props => props.size || 12}px;
  height: ${props => props.size || 12}px;
  left: ${props => props.left || 0}px;
  top: ${props => props.top || 0}px;
  transform: rotate(45deg);

  &::before,
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    margin: auto;
    background: rgba(241, 249, 244, 0.95);
    border-radius: 999px;
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.85);
  }

  &::before {
    width: 100%;
    height: 2px;
  }

  &::after {
    width: 2px;
    height: 100%;
  }
`;

const MountainWrap = styled.div`
  position: absolute;
  left: 50%;
  bottom: 62px;
  width: min(82%, 460px);
  transform: translateX(-50%);

  @media (max-width: 640px) {
    bottom: 34px;
  }
`;

const Ground = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.35);
`;

const Mountain = styled.div`
  position: relative;
  width: 76%;
  aspect-ratio: 1.15;
  margin: 0 auto -3px;
  clip-path: polygon(50% 0%, 100% 100%, 0% 100%);
  background: linear-gradient(135deg, #f8fbf8 0%, #dde4df 45%, #f7faf7 100%);
  filter: drop-shadow(0 0 14px rgba(255, 255, 255, 0.38));

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    clip-path: polygon(0 100%, 50% 0, 50% 100%);
    background: linear-gradient(180deg, rgba(205, 216, 208, 0.95) 0%, rgba(244, 248, 244, 0.96) 72%, rgba(255, 255, 255, 0.98) 100%);
  }

  &::after {
    content: '';
    position: absolute;
    left: 31%;
    right: 31%;
    bottom: 0;
    height: 44%;
    clip-path: polygon(50% 0%, 100% 100%, 0% 100%);
    background: #274e35;
  }
`;

const FormPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 56px;
  background: #ffffff;

  @media (max-width: 900px) {
    padding: 48px 28px;
  }
`;

const Form = styled.form`
  width: 100%;
  max-width: 360px;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: #131313;
  font-size: clamp(2.2rem, 4vw, 3rem);
  font-weight: 500;
  line-height: 1;
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0 0 38px;
  color: #8d8d8d;
  font-size: 1rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  margin-bottom: 18px;
  padding: 12px 14px;
  border: 1px solid #efc3c3;
  border-radius: 10px;
  color: #9d2f2f;
  background: #fff3f3;
  font-size: 0.95rem;
  text-align: center;
`;

const Field = styled.label`
  position: relative;
  display: block;
  margin-bottom: 22px;
`;

const FieldLabel = styled.span`
  position: absolute;
  top: -10px;
  left: 16px;
  padding: 0 7px;
  color: #8b8b8b;
  background: #ffffff;
  font-size: 0.95rem;
  line-height: 1;
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  height: 56px;
  padding: 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  color: #1f1f1f;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #284b31;
    box-shadow: 0 0 0 3px rgba(40, 75, 49, 0.12);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 56px;
  margin-top: 4px;
  border: 0;
  border-radius: 4px;
  background: #284b31;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: #203c27;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const LinkRow = styled.div`
  margin-top: 24px;
  text-align: center;
  color: #242424;
  font-size: 1rem;
`;

const AccentText = styled.span`
  color: #5b84ff;
`;

const SecondaryAction = styled.div`
  margin-top: 18px;
  text-align: center;
  color: #242424;
  font-size: 1rem;
  line-height: 1.5;
`;

export const Login = () => {
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const { login } = useAuthContext();
  const navigate = useNavigate();
  const { post } = useAxios();

  const handleChange = event => {
    const nextValue =
      event.target.name === 'username'
        ? usernameUtils.sanitizeInput(event.target.value)
        : event.target.value;

    setLoginData(curr => {
      return { ...curr, [event.target.name]: nextValue };
    });
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setError('');

    const normalizedUsername = usernameUtils.normalize(loginData.username);

    if (!usernameUtils.isValid(normalizedUsername)) {
      setError(usernameUtils.validationMessage);
      return;
    }

    try {
      const res = await post(`${userUrl.GET_USER}/login`, {
        ...loginData,
        username: normalizedUsername,
      });

      if (res.data.message.indexOf('successful') !== -1) {
        const userRole = res.data.user?.role || res.data.role;

        // El portal de directores es otro canal, con su propio login y su propio
        // token: mandarlos ahí es más útil que un "acceso denegado" a secas.
        if (userRole === 'director') {
          setError('Esta cuenta es de director. Entra por el portal de directores.');
          return;
        }

        if (!WEB_ROLES.includes(userRole)) {
          setError('Access denied. Only administrators can log in.');
          return;
        }

        const { password: _pw, ...safeUser } = res.data.user;
        login(safeUser, res.data.token, res.data.permissions);
        navigate('/clubes');
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('Login failed. Please check your credentials.');
    }
  };

  return (
    <Root>
      <Shell>
        <IllustrationPanel>
          <PanelGlow />
          <StarField aria-hidden='true'>
            <Star left={0} size={18} top={10} />
            <Star left={28} size={22} top={28} />
            <Star left={24} size={17} top={58} />
            <Star left={68} size={14} top={54} />
            <Star left={92} size={12} top={82} />
          </StarField>
          <Moon aria-hidden='true' />
          <MountainWrap aria-hidden='true'>
            <Mountain />
            <Ground />
          </MountainWrap>
        </IllustrationPanel>
        <FormPanel>
          <Form onSubmit={handleSubmit}>
            <Title>Login</Title>
            <Subtitle>welcome to Camp manager</Subtitle>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <Field>
              <FieldLabel>Username</FieldLabel>
              <Input
                type='text'
                name='username'
                value={loginData.username}
                onChange={handleChange}
                autoComplete='username'
                inputProps={{
                  minLength: 3,
                  maxLength: 30,
                  pattern: usernameUtils.regex.source,
                  autoCapitalize: 'none',
                }}
                required
              />
            </Field>
            <Field>
              <FieldLabel>Password</FieldLabel>
              <Input
                type='password'
                name='password'
                value={loginData.password}
                onChange={handleChange}
                autoComplete='current-password'
                required
              />
            </Field>
            <SubmitButton type='submit'>Login</SubmitButton>
            <LinkRow>
              <AccentText>Forgot password</AccentText>
            </LinkRow>
            <SecondaryAction>
              Don&apos;t have an account? <AccentText>signup</AccentText>
            </SecondaryAction>
          </Form>
        </FormPanel>
      </Shell>
    </Root>
  );
};
